import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Check,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  X,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showPostSuccess, showPostError, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ProcessingModal, ProcessingStepStatus } from '@/components/shared/ProcessingModal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { creditNotesService } from '@/services/creditNotesService'
import { financeService } from '@/services/financeService'
import { receiptReturnService } from '@/services/receiptReturnService'
import { receiptsService } from '@/services/receiptsService'
import { CreditNoteListItem, FinanceCustomer, ApiResponse, ReceiptReturnInvoice, ReceiptReturnInsertData, ReceiptReturnVm, ReceiptAccount } from '@/api/types'

export const CreditNotesList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [creditNotes, setCreditNotes] = useState<CreditNoteListItem[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [postingCreditNoteId, setPostingCreditNoteId] = useState<number | null>(null)
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [processingSteps, setProcessingSteps] = useState<
    Array<{ label: string; status: ProcessingStepStatus }>
  >([
    { label: t('invoices.preparingXml'), status: 'pending' },
    { label: t('invoices.sendingToZatca'), status: 'pending' },
    { label: t('invoices.receivingQr'), status: 'pending' },
    { label: t('invoices.updatingQr'), status: 'pending' },
  ])

  // Receipt Refund Modal State
  const [showReceiptRefundModal, setShowReceiptRefundModal] = useState(false)
  const [selectedCreditNote, setSelectedCreditNote] = useState<CreditNoteListItem | null>(null)
  const [receiptRefundLoading, setReceiptRefundLoading] = useState(false)
  const [receiptRefundSaving, setReceiptRefundSaving] = useState(false)
  const [customerInvoices, setCustomerInvoices] = useState<ReceiptReturnInvoice[]>([])
  const [insertData, setInsertData] = useState<ReceiptReturnInsertData | null>(null)
  const [accounts, setAccounts] = useState<ReceiptAccount[]>([])
  
  // Receipt Refund Form State
  const [receiptRefundForm, setReceiptRefundForm] = useState<{
    invoiceId: number
    invoiceNo: string
    destinationAccount: number
    amount: number
    date: string
    note: string
    returnType: string
  }>({
    invoiceId: 0,
    invoiceNo: '',
    destinationAccount: 0,
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    note: '',
    returnType: 'Cash',
  })

  const branchId = user?.selectedBranch?.branchId

  // Initialize date filters to current month
  useEffect(() => {
    const now = new Date()
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    
    setDateFrom(firstDay.toISOString().split('T')[0])
    setDateTo(lastDay.toISOString().split('T')[0])
  }, [])

  // Load customers for filter
  useEffect(() => {
    if (!branchId) return
    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(branchId)
        setCustomers(customerList)
      } catch (error: any) {
        console.error('Failed to load customers', error)
      }
    }
    loadCustomers()
  }, [branchId])

  // Load credit notes
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadCreditNotes = async () => {
      try {
        setLoading(true)
        const response = await creditNotesService.getAllCreditNotes(branchId, pageNumber, pageSize)
        const list = response.data || []
        
        console.log('=== Credit Notes List Loading ===')
        console.log('Loaded credit notes count:', list.length)
        console.log('Sample credit note (full):', JSON.stringify(list[0], null, 2))
        
        // Check all possible invoice ID field names
        const sampleCn = list[0]
        console.log('Checking invoice ID fields:', {
          refInvId: sampleCn?.refInvId,
          invoiceId: sampleCn?.invoiceId,
          refInvoiceId: sampleCn?.refInvoiceId,
          invId: sampleCn?.invId,
          invoiceNumber: sampleCn?.refInvNumber,
        })
        
        // Get all invoices once (more efficient)
        let invoices: any[] = []
        try {
          invoices = await creditNotesService.getAllInvoices(branchId)
          console.log('Loaded invoices for supply date mapping:', invoices.length)
          if (invoices.length > 0) {
            console.log('Sample invoice (full):', JSON.stringify(invoices[0], null, 2))
            console.log('Invoice supplyDate field:', invoices[0]?.supplyDate)
          }
        } catch (error) {
          console.warn('Failed to fetch invoices for supply date mapping', error)
        }
        
        // Map invoice supply dates to credit notes
        // Try matching by invoice ID first, then by invoice number
        const creditNotesWithInvoiceSupplyDate = list.map((cn: any) => {
          // Try different field names for invoice ID
          const invoiceId = cn.refInvId || cn.invoiceId || cn.refInvoiceId || cn.invId
          const invoiceNumber = cn.refInvNumber || cn.refInvoiceNumber || cn.invoiceNumber
          
          let matchedInvoice: any = null
          
          if (invoiceId && invoiceId > 0 && invoices.length > 0) {
            // Try matching by invoice ID
            matchedInvoice = invoices.find((inv: any) => {
              const invId = inv.invoiceId || inv.id
              return invId === invoiceId
            })
            
            if (matchedInvoice) {
              console.log(`Matched invoice by ID ${invoiceId}`)
            }
          }
          
          // If not matched by ID, try matching by invoice number
          if (!matchedInvoice && invoiceNumber && invoices.length > 0) {
            matchedInvoice = invoices.find((inv: any) => {
              const invNumber = inv.invoiceNumber || inv.invoiceNo || inv.invNumber
              return invNumber === invoiceNumber || invNumber === String(invoiceNumber)
            })
            
            if (matchedInvoice) {
              console.log(`Matched invoice by number ${invoiceNumber}`)
            }
          }
          
          if (matchedInvoice) {
            const supplyDate = matchedInvoice.supplyDate || ''
            console.log(`Found invoice, supply date:`, supplyDate)
            return {
              ...cn,
              invoiceSupplyDate: supplyDate,
            }
          } else {
            console.log(`Credit note ${cn.creditNoteId} - No invoice match. Invoice ID: ${invoiceId}, Invoice Number: ${invoiceNumber}`)
          }
          
          return cn
        })
        
        console.log('Credit notes with invoice supply dates:', creditNotesWithInvoiceSupplyDate.map(cn => ({
          cnId: cn.creditNoteId,
          refInvId: cn.refInvId,
          invoiceSupplyDate: cn.invoiceSupplyDate
        })))
        
        setCreditNotes(creditNotesWithInvoiceSupplyDate)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        console.error('Failed to load credit notes', error)
        toast.error(error.response?.data?.message || 'Failed to load credit notes')
      } finally {
        setLoading(false)
      }
    }

    loadCreditNotes()
  }, [branchId, pageNumber, pageSize])

  const filteredCreditNotes = useMemo(() => {
    return creditNotes.filter((cn) => {
      // Customer filter
      if (selectedCustomerId && cn.customerId !== selectedCustomerId) {
        return false
      }

      // Date filter
      if (dateFrom && cn.creditNoteDate) {
        const cnDate = new Date(cn.creditNoteDate)
        const fromDate = new Date(dateFrom)
        if (cnDate < fromDate) return false
      }
      if (dateTo && cn.creditNoteDate) {
        const cnDate = new Date(cn.creditNoteDate)
        const toDate = new Date(dateTo)
        toDate.setHours(23, 59, 59, 999) // Include entire end date
        if (cnDate > toDate) return false
      }

      // Search filter
      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (cn.creditNoteNumber || cn.creditNoteNo || '').toLowerCase().includes(query) ||
        (cn.customerName || '').toLowerCase().includes(query) ||
        (cn.refInvNumber || '').toLowerCase().includes(query)
      )
    })
  }, [creditNotes, selectedCustomerId, searchQuery, dateFrom, dateTo])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleAddCreditNote = () => {
    navigate('/credit-notes/new')
  }

  const handleEditCreditNote = (creditNote: CreditNoteListItem) => {
    const creditNoteId = creditNote.creditNoteId ?? creditNote.id
    if (!creditNoteId) {
      toast.error('Invalid credit note ID')
      return
    }
    navigate(`/credit-notes/${creditNoteId}/edit`, { state: { creditNote } })
  }

  const handlePostCreditNote = async (creditNote: CreditNoteListItem) => {
    const creditNoteId = creditNote.creditNoteId ?? creditNote.id
    if (!creditNoteId) {
      toast.error('Invalid credit note ID')
      return
    }
    const isPosted = creditNote.isPosted === true || creditNote.isPosted === 1
    if (isPosted) {
      toast.error('Credit note already posted')
      return
    }

    // Reset steps and show modal
    setProcessingSteps([
      { label: t('invoices.preparingXml'), status: 'pending' },
      { label: t('invoices.sendingToZatca'), status: 'pending' },
      { label: t('invoices.receivingQr'), status: 'pending' },
      { label: t('invoices.updatingQr'), status: 'pending' },
    ])
    setShowProcessingModal(true)
    setPostingCreditNoteId(creditNoteId)

    try {
      // Step 1: Preparing XML
      await new Promise((resolve) => setTimeout(resolve, 800))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 0 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Step 2: Sending to Zatca
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 1 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Step 3: Receiving QR
      await new Promise((resolve) => setTimeout(resolve, 800))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 2 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Log before making API call
      console.log('%c=== Credit Note Post Request ===', 'background: #FF5722; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
      console.log('%cCredit Note ID:', 'color: #FF9800; font-weight: bold;', creditNoteId)
      console.log('%cNote: IP Address will be retrieved automatically (logged-in user IP, not server IP)', 'color: #4CAF50; font-weight: bold;')
      console.log('%c=================================', 'background: #FF5722; color: white; padding: 5px; font-weight: bold;')

      // Make API call (IP address will be retrieved automatically in the service)
      const response = await creditNotesService.postCreditNote(creditNoteId)

      // Step 4: Updating QR
      await new Promise((resolve) => setTimeout(resolve, 500))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 3 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Wait a bit before closing modal
      await new Promise((resolve) => setTimeout(resolve, 500))

      const message =
        (response as ApiResponse)?.message ||
        (response as ApiResponse)?.data?.message ||
        (response as any)?.msg ||
        'Credit note posted successfully'
      
      setShowProcessingModal(false)
      await showPostSuccess(message)

      // Refresh list
      if (branchId) {
        const updated = await creditNotesService.getAllCreditNotes(branchId, pageNumber, pageSize)
        setCreditNotes(updated.data || [])
        setTotalCount(updated.totalRecords || 0)
        const pages = updated.totalRecords ? Math.ceil(updated.totalRecords / updated.pageSize) : 0
        setTotalPages(pages || 1)
      }

      setShowProcessingModal(false)
    } catch (error: any) {
      console.error('Failed to post credit note', error)
      
      // Mark current step as error
      setProcessingSteps((prev) => {
        const currentStepIndex = prev.findIndex((step) => step.status === 'pending')
        if (currentStepIndex !== -1) {
          return prev.map((step, idx) =>
            idx === currentStepIndex ? { ...step, status: 'error' as ProcessingStepStatus } : step
          )
        }
        return prev
      })

      // Wait a bit before closing modal and showing error
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setShowProcessingModal(false)
      await showPostError(error)
    } finally {
      setPostingCreditNoteId(null)
    }
  }

  const handlePrint = () => {
    toast.success('Print functionality coming soon')
  }

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    toast.success('CSV export coming soon')
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleCustomerFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedCustomerId(undefined)
    } else {
      setSelectedCustomerId(Number(value))
    }
    setPageNumber(1)
  }

  // Load accounts for receipt refund
  useEffect(() => {
    if (!branchId) return
    const loadAccounts = async () => {
      try {
        const accountsData = await receiptsService.getAllAccounts(branchId)
        setAccounts(accountsData || [])
      } catch (error: any) {
        console.error('Failed to load accounts', error)
      }
    }
    loadAccounts()
  }, [branchId])

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  // Handle Receipt Refund button click
  const handleReceiptRefundClick = async (creditNote: CreditNoteListItem) => {
    const customerId = creditNote.customerId
    // Try multiple possible field names for invoice ID
    const invoiceId = creditNote.refInvId || (creditNote as any).invoiceId || (creditNote as any).refInvoiceId || 0

    if (!customerId) {
      toast.error('Customer ID not found')
      return
    }

    setSelectedCreditNote(creditNote)
    setShowReceiptRefundModal(true)
    setReceiptRefundLoading(true)

    try {
      // Step 1: Get invoices by customer ID
      const invoices = await receiptReturnService.getInvoicesByCustomerId(customerId)
      setCustomerInvoices(invoices)

      if (invoices.length === 0) {
        setReceiptRefundLoading(false)
        setShowReceiptRefundModal(false)
        resetReceiptRefundForm()
        await showError('Error', t('receipts.noReceiptsFound') || 'No receipts found')
        return
      }

      // Step 2: Find matching invoice ID
      let matchedInvoiceId = invoiceId
      if (invoiceId > 0) {
        const matchedInvoice = invoices.find(inv => inv.invoiceId === invoiceId)
        if (!matchedInvoice) {
          // If invoiceId from credit note doesn't match, use first invoice
          matchedInvoiceId = invoices[0].invoiceId
        }
      } else {
        // If no invoiceId in credit note, use first invoice
        matchedInvoiceId = invoices[0].invoiceId
      }

      if (matchedInvoiceId > 0 && branchId) {
        // Step 3: Get insert data
        const insertDataResponse = await receiptReturnService.getInsertData(branchId, matchedInvoiceId)
        setInsertData(insertDataResponse)

        // Step 4: Populate form
        const matchedInvoice = invoices.find(inv => inv.invoiceId === matchedInvoiceId)
        setReceiptRefundForm({
          invoiceId: matchedInvoiceId,
          invoiceNo: matchedInvoice?.invoiceNo || '',
          destinationAccount: insertDataResponse.receiptReturn.destinationAccount,
          amount: insertDataResponse.receiptReturn.amount,
          date: new Date().toISOString().split('T')[0],
          note: '',
          returnType: 'Cash',
        })
      } else {
        toast.error('Unable to determine invoice ID')
      }
    } catch (error: any) {
      console.error('Failed to load receipt refund data', error)
      await showError('Error', error.response?.data?.message || 'Failed to load receipt refund data')
    } finally {
      setReceiptRefundLoading(false)
    }
  }

  // Handle Receipt Refund Save
  const handleReceiptRefundSave = async () => {
    if (!branchId || !selectedCreditNote) return

    if (!receiptRefundForm.destinationAccount || receiptRefundForm.destinationAccount === 0) {
      toast.error('Please select destination account')
      return
    }

    if (!receiptRefundForm.amount || receiptRefundForm.amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (!receiptRefundForm.date) {
      toast.error('Please select a date')
      return
    }

    if (!receiptRefundForm.returnType) {
      toast.error('Please select return type')
      return
    }

    setReceiptRefundSaving(true)

    try {
      // Get user IP address
      const ipAddress = await getUserIPAddress()

      const saveData: ReceiptReturnVm = {
        id: 0,
        invoiceId: receiptRefundForm.invoiceId,
        customerId: selectedCreditNote.customerId || 0,
        destinationAccount: receiptRefundForm.destinationAccount,
        amount: receiptRefundForm.amount,
        date: new Date(receiptRefundForm.date).toISOString(),
        note: receiptRefundForm.note,
        returnType: receiptRefundForm.returnType,
        ipAddress: ipAddress,
      }

      // Log the exact structure being sent
      console.log('=== Receipt Refund Save Request ===')
      console.log('Save Data Structure:', JSON.stringify(saveData, null, 2))
      console.log('Save Data Object:', saveData)
      console.log('===================================')

      await receiptReturnService.saveReceiptReturn(saveData)
      await showSuccess('Success', 'Receipt refund saved successfully')
      setShowReceiptRefundModal(false)
      resetReceiptRefundForm()
    } catch (error: any) {
      console.error('❌ Failed to save receipt refund', error)
      console.error('❌ Error Response:', error.response)
      console.error('❌ Error Response Data:', error.response?.data)
      console.error('❌ Error Message:', error.message)
      console.error('❌ Error Stack:', error.stack)
      
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.response?.data?.title ||
        error.message || 
        'Failed to save receipt refund'
      
      await showError('Error', errorMessage)
    } finally {
      setReceiptRefundSaving(false)
    }
  }

  // Reset receipt refund form
  const resetReceiptRefundForm = () => {
    setReceiptRefundForm({
      invoiceId: 0,
      invoiceNo: '',
      destinationAccount: 0,
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      note: '',
      returnType: 'Cash',
    })
    setSelectedCreditNote(null)
    setCustomerInvoices([])
    setInsertData(null)
  }

  // Payment type options (same as receipts)
  const returnTypeOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Bank', label: 'Bank' },
  ]

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Sales Credit Notes"
          description="Manage your credit notes"
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="Branch Not Selected"
              description="Please select a branch to view credit notes."
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Processing Modal */}
      <ProcessingModal open={showProcessingModal} steps={processingSteps} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales</h1>
          <p className="text-muted-foreground">Credit Notes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleAddCreditNote}>
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Credit Note Date From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Credit Note Date To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Customer</label>
              <Select value={selectedCustomerId ? String(selectedCustomerId) : 'all'} onValueChange={handleCustomerFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.customerId} value={String(customer.customerId)}>
                      {customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Notes Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading credit notes...</p>
            </div>
          ) : filteredCreditNotes.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedCustomerId || dateFrom || dateTo ? 'No credit notes found' : 'No credit notes yet'}
                description={
                  searchQuery || selectedCustomerId || dateFrom || dateTo
                    ? 'Try adjusting your search or filter'
                    : 'Get started by creating your first credit note.'
                }
                action={
                  !searchQuery && !selectedCustomerId && !dateFrom && !dateTo
                    ? {
                        label: 'Add New Credit Note',
                        onClick: handleAddCreditNote,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                    <TableHead className={`font-bold text-primary px-3 py-3 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('creditNotes.creditNoteNumber') || 'CN #'}</TableHead>
                    <TableHead className={`font-bold text-primary px-3 py-3 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('creditNotes.refInvNumber') || 'Ref Inv #'}</TableHead>
                    <TableHead className={`font-bold text-primary px-3 py-3 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('creditNotes.customer') || 'Customer'}</TableHead>
                    <TableHead className={`font-bold text-primary px-3 py-3 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('creditNotes.date') || 'Date'}</TableHead>
                    <TableHead className="font-bold text-primary text-right px-3 py-3">{t('creditNotes.amount') || 'Amount'}</TableHead>
                    <TableHead className={`font-bold text-primary px-3 py-3 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('creditNotes.posted') || 'Posted'}</TableHead>
                    <TableHead className="font-bold text-primary text-center px-3 py-3">{t('creditNotes.eInv') || 'E-INV'}</TableHead>
                    <TableHead className="font-bold text-primary text-center px-3 py-3">{t('common.edit') || 'Edit'}</TableHead>
                    <TableHead className="font-bold text-primary text-center px-3 py-3">{t('creditNotes.post') || 'Post'}</TableHead>
                    <TableHead className="font-bold text-primary text-center px-3 py-3">{t('creditNotes.receiptRefund') || 'Receipt Refund'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCreditNotes.map((creditNote) => {
                    const creditNoteId = creditNote.creditNoteId ?? creditNote.id
                    const isPosted = creditNote.isPosted === true || creditNote.isPosted === 1
                    return (
                      <TableRow key={creditNoteId} className="border-b hover:bg-primary/5 transition-colors">
                        <TableCell className={`font-medium px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>
                          {creditNote.creditNoteNumber || creditNote.creditNoteNo || `CN-${creditNoteId}`}
                        </TableCell>
                        <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>
                          {(creditNote as any).refNo || creditNote.refInvNumber || '-'}
                        </TableCell>
                        <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{creditNote.customerName || 'N/A'}</TableCell>
                        <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{formatDate(creditNote.creditNoteDate)}</TableCell>
                        <TableCell className="text-right px-3 py-3">
                          {formatAmount(creditNote.amount ?? creditNote.total)}
                        </TableCell>
                        <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>
                          <Badge variant={isPosted ? 'default' : 'secondary'}>
                            {isPosted ? 'Posted' : 'Not Posted'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center px-3 py-3">
                          <Button variant="ghost" size="icon" onClick={handlePrint}>
                            <Printer className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center px-3 py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditCreditNote(creditNote)}
                            title={isPosted ? 'Credit note already posted' : 'Edit credit note'}
                            disabled={isPosted}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center px-3 py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePostCreditNote(creditNote)}
                            disabled={isPosted || postingCreditNoteId === creditNoteId}
                            className={isPosted ? 'text-green-600' : ''}
                          >
                            {postingCreditNoteId === creditNoteId ? (
                              <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></span>
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-center px-3 py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReceiptRefundClick(creditNote)}
                            title="Receipt Refund"
                          >
                            <ArrowLeftRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="border-t px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="text-sm">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Receipt Refund Modal */}
      {showReceiptRefundModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Receipt Refund</h3>
              <button
                onClick={() => {
                  setShowReceiptRefundModal(false)
                  resetReceiptRefundForm()
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                disabled={receiptRefundSaving}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {receiptRefundLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
                </div>
              ) : (
                <>
                  {/* Invoice Dropdown (Disabled) */}
                  <div className="space-y-2">
                    <Label htmlFor="invoice" className="text-gray-700 dark:text-gray-300 font-medium">
                      Invoice
                    </Label>
                    <Select
                      value={String(receiptRefundForm.invoiceId)}
                      disabled
                    >
                      <SelectTrigger id="invoice" className="bg-gray-50 dark:bg-gray-800">
                        <SelectValue placeholder="Select invoice" />
                      </SelectTrigger>
                      <SelectContent>
                        {customerInvoices.map((invoice) => (
                          <SelectItem key={invoice.invoiceId} value={String(invoice.invoiceId)}>
                            {invoice.invoiceNo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Destination Account Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="destinationAccount" className="text-gray-700 dark:text-gray-300 font-medium">
                      Destination Account *
                    </Label>
                    <Select
                      value={String(receiptRefundForm.destinationAccount)}
                      onValueChange={(value) =>
                        setReceiptRefundForm((prev) => ({
                          ...prev,
                          destinationAccount: Number(value),
                        }))
                      }
                      disabled={receiptRefundSaving}
                    >
                      <SelectTrigger id="destinationAccount">
                        <SelectValue placeholder="Select destination account" />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((account) => {
                          const accountId = account.accountId || account.id || 0
                          const accountName = account.accountNameEn || account.accountName || account.accountNameAr || ''
                          const accountCode = account.accountCode || ''
                          const displayLabel = accountCode ? `${accountCode} - ${accountName}` : accountName
                          return (
                            <SelectItem key={accountId} value={String(accountId)}>
                              {displayLabel}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="amount" className="text-gray-700 dark:text-gray-300 font-medium">
                      Amount *
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={receiptRefundForm.amount}
                      onChange={(e) =>
                        setReceiptRefundForm((prev) => ({
                          ...prev,
                          amount: Number(e.target.value),
                        }))
                      }
                      disabled={receiptRefundSaving}
                      max={insertData?.maxReturnAmount}
                    />
                    {insertData && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Max return amount: {formatAmount(insertData.maxReturnAmount)}
                      </p>
                    )}
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-gray-700 dark:text-gray-300 font-medium">
                      Date *
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      value={receiptRefundForm.date}
                      onChange={(e) =>
                        setReceiptRefundForm((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                      disabled={receiptRefundSaving}
                    />
                  </div>

                  {/* Note */}
                  <div className="space-y-2">
                    <Label htmlFor="note" className="text-gray-700 dark:text-gray-300 font-medium">
                      Note
                    </Label>
                    <Input
                      id="note"
                      type="text"
                      value={receiptRefundForm.note}
                      onChange={(e) =>
                        setReceiptRefundForm((prev) => ({
                          ...prev,
                          note: e.target.value,
                        }))
                      }
                      disabled={receiptRefundSaving}
                      placeholder="Enter note"
                    />
                  </div>

                  {/* Return Type */}
                  <div className="space-y-2">
                    <Label htmlFor="returnType" className="text-gray-700 dark:text-gray-300 font-medium">
                      Return Type *
                    </Label>
                    <Select
                      value={receiptRefundForm.returnType}
                      onValueChange={(value) =>
                        setReceiptRefundForm((prev) => ({
                          ...prev,
                          returnType: value,
                        }))
                      }
                      disabled={receiptRefundSaving}
                    >
                      <SelectTrigger id="returnType">
                        <SelectValue placeholder="Select return type" />
                      </SelectTrigger>
                      <SelectContent>
                        {returnTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Modal Actions */}
                  <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowReceiptRefundModal(false)
                        resetReceiptRefundForm()
                      }}
                      className="flex-1"
                      disabled={receiptRefundSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleReceiptRefundSave}
                      className="flex-1 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white"
                      disabled={receiptRefundSaving}
                    >
                      {receiptRefundSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

